// Libs
import { uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "src/design-system/core/TranslationContext";

// Types
import type { IdOfferTypeEnumName } from "src/types/IdName";
import { OfferTypeEnum } from "src/types/offer";

const OFFER_TYPE_DEFAULT_VALUE = [];

export interface OfferTypeFilter {
    id: number;
    name: string;
    key: string;
}

export type UseOfferTypeFilterValue = OfferTypeEnum[];

export interface UseOfferTypeFilterOptions {
    defaultValue?: UseOfferTypeFilterValue;
    initialValue?: UseOfferTypeFilterValue;
    offerTypesOptions?: UseOfferTypeFilterValue;
}

export interface UseOfferTypeFilterResult {
    handleOfferTypeChange: (offerType: UseOfferTypeFilterValue) => void;
    isEmpty: boolean;
    resetFilter: () => void;
    offerTypes: UseOfferTypeFilterValue;
    offerTypesOptions: IdOfferTypeEnumName[];
}

const SPECIFIC_DISH_OFFER_TYPES = [OfferTypeEnum.SpecificDish, OfferTypeEnum.DiscountByItem];

/**
 * Hook to handle a OfferType filter
 * @param options Hooks options
 * @returns Helpers to handle OfferType filter
 */
export function useOfferTypeFilter({
    defaultValue = OFFER_TYPE_DEFAULT_VALUE,
    initialValue,
    offerTypesOptions: rawOfferTypesOptions = [],
}: UseOfferTypeFilterOptions): UseOfferTypeFilterResult {
    const { translateCommon } = useTranslation();

    const [offerTypes, setOfferTypes] = useState<UseOfferTypeFilterValue>(
        initialValue ?? defaultValue
    );

    const offerTypesOptions = useMemo(
        () =>
            rawOfferTypesOptions
                .filter((ot) => ot !== OfferTypeEnum.DiscountByItem)
                .map((ot) => ({ id: ot, name: translateCommon(`enums.offer-type.${ot}`) })),
        [rawOfferTypesOptions]
    );

    const handleOfferTypeChange = useCallback(
        (selectedOfferTypesOptions: UseOfferTypeFilterValue) => {
            let selectedOfferTypes = [...selectedOfferTypesOptions];
            const hasSpecificDish = selectedOfferTypes.find(
                (ot) => ot === OfferTypeEnum.SpecificDish
            );

            if (hasSpecificDish) {
                selectedOfferTypes.push(...SPECIFIC_DISH_OFFER_TYPES);
            } else {
                selectedOfferTypes = selectedOfferTypes.filter(
                    (ot) => !SPECIFIC_DISH_OFFER_TYPES.includes(ot)
                );
            }

            setOfferTypes(uniq(selectedOfferTypes));
        },
        [setOfferTypes, SPECIFIC_DISH_OFFER_TYPES]
    );

    const resetFilter = useCallback(
        () => setOfferTypes(defaultValue),
        [setOfferTypes, defaultValue]
    );

    return {
        handleOfferTypeChange,
        isEmpty: isOfferTypeFilterEmpty(offerTypes),
        resetFilter,
        offerTypesOptions,
        offerTypes,
    };
}

/**
 * Format OfferType filter value
 * @param offerTypes Filter value
 * @param offerTypeOptions OfferType options
 * @returns Formatted filter value
 */
export function formatOfferTypeFilter(
    offerTypes: UseOfferTypeFilterValue = OFFER_TYPE_DEFAULT_VALUE,
    offerTypeOptions: IdOfferTypeEnumName[] = []
): string {
    return offerTypes
        .filter((ot) => ot !== OfferTypeEnum.DiscountByItem)
        .map((ot) => offerTypeOptions.find((otOpt) => otOpt.id === ot).name)
        .join(", ");
}

/**
 * Compute if filter is empty or not
 * @param filter Filter value
 * @returns If is empty
 */
export function isOfferTypeFilterEmpty(
    filter: UseOfferTypeFilterValue = OFFER_TYPE_DEFAULT_VALUE
): boolean {
    return filter.length === 0;
}
